<template>
	<div
		:class="[
			$style['app-table-col'],
			{
				[$style['app-table-col--active']]: active && sortable,
				[$style['app-table-col--header']]: header,
				[$style['app-table-col--header--sortable']]: header && sortable,
				[$style['app-table-col--disabled']]: empty,
				[$style['app-table-col--compact']]: compact,
				[$style['app-table-col--sticky']]: stickyLeft !== null && stickyLeft !== undefined,
				[$style['app-table-col--sticky-last']]: isLastSticky,
				[$style['app-table-col--sticky-align-cross']]: hasStickyAlign && stickyAlignAxis === 'cross',
				[$style['app-table-col--sticky-align-main']]: hasStickyAlign && stickyAlignAxis === 'main',
				[$style['app-table-col--resizing']]: isResizing
			}
		]"
		:style="computedStyle"
		@click="validateClick"
	>
		<template v-if="!empty">
			<span v-if="header && movable" class="columnHandle" :class="$style['app-table-col__label']"><slot /></span>
			<span v-else-if="header" :class="$style['app-table-col__label']"><slot /></span>
			<slot v-else />
			<template v-if="sortable">
				<FontAwesomeIcon
					v-if="header && active && sortOrder === 'desc'"
					:icon="['far', 'arrow-down']"
					:class="$style['app-table-col__arrow']"
				/>
				<FontAwesomeIcon
					v-else-if="header && active && sortOrder === 'asc'"
					:icon="['far', 'arrow-up']"
					:class="$style['app-table-col__arrow']"
				/>
			</template>
		</template>
		<div
			v-if="canResize"
			:class="$style['app-table-col__resize-handle']"
			@click.stop.prevent
			@mousedown.stop.prevent="startResize"
			@touchstart.stop.prevent="startResize"
		/>
	</div>
</template>
<script>
import breakpoints from '@/mixins/breakpoints'
import { getColumnMinWidth, getColumnResizedWidth } from '@/utils/tableColumns'

export default {
	name: 'BaseTableCol',
	mixins: [breakpoints],
	props: {
		column: {
			type: Object,
			default: null
		},
		widthPreset: {
			type: String,
			default: null
		},
		minWidth: {
			type: Number,
			default: null
		},
		resizedWidth: {
			type: [Number, String],
			default: null
		},
		resizable: {
			type: [Boolean, String],
			default: null
		},
		span: {
			type: [Number, String],
			default: 1
		},
		header: {
			type: [Boolean, String],
			default: false
		},
		sortable: {
			type: Boolean,
			default: true
		},
		active: {
			type: [Boolean, String],
			default: false
		},
		empty: {
			type: [Boolean, String],
			default: false
		},
		compact: {
			type: Boolean,
			default: false
		},
		movable: {
			type: Boolean,
			default: true
		},
		sortOrder: {
			type: String,
			validator: (value) => ['asc', 'desc'].includes(value),
			default: 'desc'
		}
	},
	data () {
		return {
			columnWidth: 0,
			resizePreviewWidth: null,
			isResizing: false,
			stickyLeft: null,
			stickyAlign: '',
			stickyAlignAxis: '',
			isLastSticky: false
		}
	},

	computed: {
		resolvedMinWidth () {
			return getColumnMinWidth({
				...this.column,
				widthPreset: this.widthPreset || this.column?.widthPreset,
				minWidth: this.minWidth ?? this.column?.minWidth
			})
		},
		resolvedResizedWidth () {
			return getColumnResizedWidth({
				resizedWidth: this.resizedWidth ?? this.column?.resizedWidth
			})
		},
		canResize () {
			const resizable = this.resizable === null || this.resizable === undefined
				? !!this.column
				: this.resizable !== false && this.resizable !== 'false'
			return Boolean(this.header && resizable)
		},
		hasStickyAlign () {
			return this.stickyLeft !== null && this.stickyLeft !== undefined && !!this.stickyAlign
		},
		computedStyle () {
			const styles = [`min-width: ${this.resolvedMinWidth}px`]
			const width = this.resizePreviewWidth ?? this.columnWidth
			if (width) {
				styles.push(`flex: 0 0 ${Math.max(this.resolvedMinWidth, width)}px`)
			}
			if (this.stickyLeft !== null && this.stickyLeft !== undefined) {
				styles.push(`position: sticky; left: ${this.stickyLeft}; z-index: 1; align-self: stretch`)
			}
			if (this.hasStickyAlign) {
				styles.push(`--sticky-align: ${this.stickyAlign}`)
			}
			return styles.join('; ')
		}
	},

	beforeDestroy () {
		if (this.$_resizeRafId) {
			cancelAnimationFrame(this.$_resizeRafId)
		}
		if (this.$_suppressClickTimeoutId) {
			clearTimeout(this.$_suppressClickTimeoutId)
			this.$_suppressClickTimeoutId = null
		}
		this.cleanupResize()
	},

	methods: {
		validateClick (e) {
			if (this.$_suppressClick) {
				e.stopPropagation()
				e.preventDefault()
				this.$_suppressClick = false
				return
			}

			if (this.header && !this.sortable) {
				e.stopPropagation()
				e.preventDefault()
			} else {
				this.$emit('click', e)
			}
		},

		getClientX (e) {
			if (e.touches && e.touches.length) return e.touches[0].clientX
			if (e.changedTouches && e.changedTouches.length) return e.changedTouches[0].clientX
			return e.clientX
		},

		startResize (e) {
			const el = this.$el
			if (!el) return
			const rect = el.getBoundingClientRect()
			if (this.$_suppressClickTimeoutId) {
				clearTimeout(this.$_suppressClickTimeoutId)
				this.$_suppressClickTimeoutId = null
			}

			this.$_resizeStartX = this.getClientX(e)
			this.$_resizeStartWidth = rect.width
			this.$_resizeMaxWidth = Infinity
			this.$_resizePendingWidthPx = rect.width
			this.$_resizeDidMove = false
			this.$_suppressClick = false
			this.$_resizeRafId = null
			this.$_resizeOwnedCursor = true
			this.isResizing = true

			this.$_resizeMoveFn = this.onResizeMove.bind(this)
			this.$_resizeUpFn = this.stopResize.bind(this)
			document.addEventListener('mousemove', this.$_resizeMoveFn)
			document.addEventListener('mouseup', this.$_resizeUpFn)
			document.addEventListener('touchmove', this.$_resizeMoveFn, { passive: false })
			document.addEventListener('touchend', this.$_resizeUpFn)
			document.addEventListener('touchcancel', this.$_resizeUpFn)
			document.body.style.cursor = 'col-resize'
			document.body.style.userSelect = 'none'
			this.$emit('resize-start')
		},

		onResizeMove (e) {
			if (e.cancelable) e.preventDefault()
			const delta = this.getClientX(e) - this.$_resizeStartX
			if (delta !== 0) this.$_resizeDidMove = true
			this.$_resizePendingWidthPx = Math.max(this.resolvedMinWidth,
				Math.min(this.$_resizeMaxWidth, this.$_resizeStartWidth + delta))

			if (this.$_resizeRafId) return
			this.$_resizeRafId = requestAnimationFrame(() => {
				this.$_resizeRafId = null
				this.resizePreviewWidth = this.$_resizePendingWidthPx
			})
		},

		stopResize (event) {
			if (this.$_resizeRafId) {
				cancelAnimationFrame(this.$_resizeRafId)
				this.$_resizeRafId = null
			}
			const changed = this.$_resizeDidMove && event?.type !== 'touchcancel' &&
				Math.abs(this.$_resizePendingWidthPx - this.$_resizeStartWidth) >= 0.5
			this.resizePreviewWidth = null
			this.$emit('resize-col', changed ? this.$_resizePendingWidthPx : null)
			if (this.$_resizeDidMove) {
				this.$_suppressClick = true
				if (this.$_suppressClickTimeoutId) {
					clearTimeout(this.$_suppressClickTimeoutId)
				}
				this.$_suppressClickTimeoutId = setTimeout(() => {
					this.$_suppressClick = false
					this.$_suppressClickTimeoutId = null
				}, 0)
			}
			this.cleanupResize()
		},

		cleanupResize () {
			this.isResizing = false
			if (this.$_resizeMoveFn) {
				document.removeEventListener('mousemove', this.$_resizeMoveFn)
				document.removeEventListener('touchmove', this.$_resizeMoveFn)
				this.$_resizeMoveFn = null
			}
			if (this.$_resizeUpFn) {
				document.removeEventListener('mouseup', this.$_resizeUpFn)
				document.removeEventListener('touchend', this.$_resizeUpFn)
				document.removeEventListener('touchcancel', this.$_resizeUpFn)
				this.$_resizeUpFn = null
			}
			if (this.$_resizeOwnedCursor) {
				document.body.style.cursor = ''
				document.body.style.userSelect = ''
				this.$_resizeOwnedCursor = false
			}
		}
	}
}
</script>
<style lang="scss" module>

.app-table-col {
	display: flex;
	flex-flow: row;
	justify-content: flex-start;
	align-items: flex-start;
	white-space: normal;
	overflow-wrap: anywhere;
	flex: 1;
	padding: 20px 15px;
	width: 100%;
	min-width: 0;
	overflow: visible;
	isolation: isolate;

	:global(.is-dropdown-open) & {
		isolation: auto;
	}

	@include media-breakpoint-only(xs) {
		padding: 5px 10px;
	}

	span:not(#{&}__label) {
		overflow-wrap: break-word;
		white-space: normal;
		overflow: visible;
		text-overflow: unset;
		min-width: 0;
		max-width: 100%;
	}

	&__label {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		min-width: 0;
		flex-shrink: 1;
	}

	&__arrow {
		visibility: hidden;
		margin-left: 7.5px;
		width: 1em;
		flex-shrink: 0;
	}

	&--header {
		user-select: none;
		flex-flow: row;
		align-items: center;
		white-space: nowrap;
		overflow: visible;
		position: relative;

		&:hover {
			cursor: grab;
			color: getColor(text);
			font-weight: bold;

			.app-table-col__arrow {
				visibility: visible;
			}
		}

		&--sortable {
			&:hover {
				cursor: pointer;
			}
		}
	}

	&--active {
		color: getColor(text);
		letter-spacing: 0;
		font-weight: bold;

		.app-table-col__arrow {
			visibility: visible;
		}
	}

	&--disabled {
		&:hover {
			cursor: default !important;
		}
	}

	&--compact {
		padding: $gutter;
	}

	&--sticky {
		background-color: transparent;
	}

	:global(.is-scrolled-x) &--sticky {
		background-color: var(--row-bg, #{getColor(surface)});

		@include dark-mode {
			background-image: var(--sticky-overlay, linear-gradient(rgb(255 255 255 / 0.03), rgb(255 255 255 / 0.03)));
		}
	}

	@include media-breakpoint-only(xs) {
		&--sticky,
		:global(.is-scrolled-x) &--sticky {
			position: relative !important;
			left: auto !important;
			background-color: transparent;
			background-image: none !important;
		}

		&--sticky-last::after {
			display: none;
		}
	}

	&--sticky-align-cross {
		align-items: var(--sticky-align) !important;
	}

	&--sticky-align-main {
		justify-content: var(--sticky-align) !important;
	}

	&--resizing {
		.app-table-col__resize-handle::after {
			opacity: 1;
		}
	}

	&--sticky-last {
		&::after {
			content: '';
			position: absolute;
			top: 0;
			bottom: 0;
			left: 100%;
			width: 8px;
			background: linear-gradient(to right, transparentize(getColor(shadow-light), 0.5), transparent);
			opacity: 0;
			transition: opacity 0.12s ease;
			pointer-events: none;

			@include dark-mode {
				display: none;
			}
		}
	}

	:global(.is-scrolled-x) &--sticky-last {
		&::after {
			opacity: var(--sticky-edge-opacity, 1);
		}
	}

	&__resize-handle {
		position: absolute;
		right: 0;
		top: 0;
		bottom: 0;
		width: 6px;
		cursor: col-resize;
		z-index: 1;
		display: flex;
		align-items: center;
		justify-content: center;

		&::after {
			content: '';
			display: block;
			width: 2px;
			height: 60%;
			background-color: getColor(border-common);
			border-radius: 1px;
			opacity: 0;
			transition: opacity 0.15s ease;
		}

		&:hover::after {
			opacity: 1;
		}

		@include media-breakpoint-only(xs) {
			width: 32px;
			right: -13px;

			&::after {
				opacity: 1;
			}
		}
	}
}
</style>
